/**
 * COMPOSANT: GlobalTimeline
 * Affichage de l'historique global de tous les événements de l'organisation avec pagination
 */

import { useState, useMemo, useRef, useCallback } from 'react';
import { useGlobalTimeline } from '@/hooks/useGlobalTimeline';
import { TimelineEvent as TimelineEventComponent } from './TimelineEvent';
import { Card } from '../ui/card';
import { RefreshCw, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Timeline globale affichant tous les événements de l'organisation
 * Pas de filtre utilisateur - montre l'activité de toute l'équipe
 */
export function GlobalTimeline() {
  const { events, loading, error, hasMore, refresh, loadMore } = useGlobalTimeline();
  const [searchTerm, setSearchTerm] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filtrer événements par recherche
  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return events;

    const term = searchTerm.toLowerCase();
    return events.filter(
      (event) =>
        event.title.toLowerCase().includes(term) ||
        event.description?.toLowerCase().includes(term) ||
        event.event_type.toLowerCase().includes(term)
    );
  }, [events, searchTerm]);

  // Gérer le chargement de plus d'événements en préservant la position de scroll
  const handleLoadMore = useCallback(async () => {
    if (!scrollContainerRef.current || isLoadingMore) return;

    setIsLoadingMore(true);

    // Sauvegarder la position de scroll actuelle
    const scrollContainer = scrollContainerRef.current;
    const scrollTopBefore = scrollContainer.scrollTop;

    // Charger plus d'événements
    await loadMore();

    // Attendre que le DOM soit mis à jour
    requestAnimationFrame(() => {
      if (scrollContainer) {
        // Restaurer exactement la même position de scroll
        // (les nouveaux éléments sont ajoutés en bas, donc la position relative reste la même)
        scrollContainer.scrollTop = scrollTopBefore;
      }
      setIsLoadingMore(false);
    });
  }, [loadMore, isLoadingMore]);

  return (
    <Card className="flex h-full flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-zinc-900">Historique global</h3>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} title="Actualiser">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <Input
          type="text"
          placeholder="Rechercher dans l'historique..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Liste des événements */}
      <div ref={scrollContainerRef} className="flex-1 space-y-0 overflow-y-auto">
        {/* État de chargement */}
        {loading && events.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-zinc-500">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Chargement de l'historique...</span>
            </div>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Erreur lors du chargement de l'historique
          </div>
        )}

        {/* Liste des événements */}
        {!loading && !error && filteredEvents.length > 0 && (
          <>
            {filteredEvents.map((event, index) => (
              <TimelineEventComponent
                key={event.id}
                event={event}
                isLast={index === filteredEvents.length - 1}
              />
            ))}
          </>
        )}

        {/* Aucun événement */}
        {!loading && !error && filteredEvents.length === 0 && events.length === 0 && (
          <div className="py-12 text-center text-sm text-zinc-500">
            Aucun événement pour le moment
          </div>
        )}

        {/* Aucun résultat de recherche */}
        {!loading && !error && filteredEvents.length === 0 && events.length > 0 && (
          <div className="py-12 text-center text-sm text-zinc-500">
            Aucun résultat pour "{searchTerm}"
          </div>
        )}

        {/* Bouton Charger plus */}
        {!loading && !error && hasMore && (
          <div className="flex justify-center pt-4 pb-2">
            <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? 'Chargement...' : 'Charger plus'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
